import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Heart, Flame, BookOpen, Brain, Users, Sparkles, ArrowRight, ChevronDown, Menu, X, Calendar, Target, Apple, Map, MessageCircle, Globe, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { LogoFull, LogoSmall } from '@/components/Logo';
import { LanguageSelector } from '@/components/LanguageSelector';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import FeatureCarousel from '@/components/FeatureCarousel';
import { useMultilingualSEO } from '@/hooks/useMultilingualSEO';

const KRISHNA_IMG = "/krishna-flute.webp";

const SANSKRIT_VERSES = [
  "कर्मण्येवाधिकारस्ते मा फलेषु कदाचन ।\nमा कर्मफलहेतुर्भूर्मा ते सङ्गोऽस्त्वकर्मणि ॥",
  "योगस्थः कुरु कर्माणि सङ्गं त्यक्त्वा धनञ्जय ।\nसिद्ध्यसिद्ध्योः समो भूत्वा समत्वं योग उच्यते ॥",
  "वासांसि जीर्णानि यथा विहाय नवानि गृह्णाति नरोऽपराणि ।\nतथा शरीराणि विहाय जीर्णान्यन्यानि संयाति नवानि देही ॥",
  "यदा यदा हि धर्मस्य ग्लानिर्भवति भारत ।\nअभ्युत्थानमधर्मस्य तदात्मानं सृजाम्यहम् ॥",
  "नैनं छिन्दन्ति शस्त्राणि नैनं दहति पावकः ।\nन चैनं क्लेदयन्त्यापो न शोषयति मारुतः ॥",
  "सर्वधर्मान्परित्यज्य मामेकं शरणं व्रज ।\nअहं त्वां सर्वपापेभ्यो मोक्षयिष्यामि मा शुचः ॥",
  "उद्धरेदात्मनात्मानं नात्मानमवसादयेत् ।\nआत्मैव ह्यात्मनो बन्धुरात्मैव रिपुरात्मनः ॥",
  "श्रद्धावान्लभते ज्ञानं तत्परः संयतेन्द्रियः ।\nज्ञानं लब्ध्वा परां शान्तिमचिरेणाधिगच्छति ॥",
  "नेहाभिक्रमनाशोऽस्ति प्रत्यवायो न विद्यते ।\nस्वल्पमप्यस्य धर्मस्य त्रायते महतो भयात् ॥",
  "परित्राणाय साधूनां विनाशाय च दुष्कृताम् ।\nधर्मसंस्थापनार्थाय सम्भवामि युगे युगे ॥",
];

const features = [
  { icon: Flame, titleKey: "landing.feat_trigger_title", descKey: "landing.feat_trigger_desc" },
  { icon: Brain, titleKey: "landing.feat_guidance_title", descKey: "landing.feat_guidance_desc" },
  { icon: BookOpen, titleKey: "landing.feat_programs_title", descKey: "landing.feat_programs_desc" },
  { icon: Heart, titleKey: "landing.feat_guna_title", descKey: "landing.feat_guna_desc" },
  { icon: Sparkles, titleKey: "landing.feat_daily_title", descKey: "landing.feat_daily_desc" },
  { icon: Users, titleKey: "landing.feat_family_title", descKey: "landing.feat_family_desc" },
];

function RotatingVerse() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * SANSKRIT_VERSES.length));
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const cycle = () => {
      setVisible(false);
      setTimeout(() => {
        setIndex(prev => {
          let next;
          do { next = Math.floor(Math.random() * SANSKRIT_VERSES.length); } while (next === prev);
          return next;
        });
        setVisible(true);
      }, 600);
    };
    const id = setInterval(cycle, 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-[3.5rem] md:min-h-[4.5rem] flex items-center mb-3" data-testid="rotating-verse">
      <p
        className="font-['Noto_Sans_Devanagari'] text-base md:text-xl text-primary/70 leading-relaxed transition-opacity duration-500 ease-in-out select-none whitespace-pre-line"
        style={{ opacity: visible ? 1 : 0 }}
      >
        {SANSKRIT_VERSES[index]}
      </p>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [dynamicPrograms, setDynamicPrograms] = useState([]);
  const { getCanonicalUrl, generateHreflangTags, currentLangConfig, SITE_URL } = useMultilingualSEO('landing');

  useEffect(() => {
    const API = '';
    fetch(`${API}/api/public/programs`)
      .then(r => r.json())
      .then(d => setDynamicPrograms(d.programs || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-hidden" data-testid="landing-page">
      <Helmet>
        <title>{t('seo.landing_title')}</title>
        <meta name="description" content={t('seo.landing_desc')} />
        <link rel="canonical" href="https://livinggeeta.com/" />
        {generateHreflangTags('/')}
        <meta property="og:title" content={t('seo.landing_title')} />
        <meta property="og:description" content={t('seo.landing_desc')} />
        <meta property="og:url" content="https://livinggeeta.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={t('seo.landing_title')} />
        <meta name="twitter:description" content={t('seo.landing_desc')} />
      </Helmet>
      {/* Nav */}
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <RotatingVerse />
            <p className="text-sm uppercase tracking-[0.2em] text-primary/70 mb-4 font-medium animate-fade-in-up">
              {t('landing.hero_subtitle')}
            </p>
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-semibold tracking-tight leading-tight text-foreground mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              {t('landing.hero_title_1')}<br />
              <span className="text-primary">{t('landing.hero_title_2')}</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-lg mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {t('landing.hero_desc')}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <Link to="/signup" className="bg-primary text-primary-foreground rounded-full px-8 py-3.5 font-medium hover:shadow-lg hover:shadow-primary/20 transition-all hover:-translate-y-0.5 flex items-center gap-2" data-testid="hero-cta-btn">
                {t('landing.cta_start')} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#features" className="border border-primary/20 text-foreground rounded-full px-8 py-3.5 font-medium hover:bg-primary/5 transition-all flex items-center gap-2" data-testid="learn-more-btn">
                {t('common.learn_more')} <ChevronDown className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 rounded-3xl blur-3xl" />
            <img
              src="/krishna-flute-800.webp"
              srcSet="/krishna-flute-350.webp 350w, /krishna-flute-mobile.webp 400w, /krishna-flute-800.webp 800w"
              sizes="(max-width: 480px) 350px, (max-width: 768px) 400px, 800px"
              alt="Lord Krishna playing the divine flute - Bhagavad Gita wisdom for modern living"
              className="relative w-full max-w-lg mx-auto rounded-3xl shadow-2xl"
              data-testid="hero-image"
              width={800}
              height={1200}
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>

        {/* Quote */}
        <div className="max-w-3xl mx-auto mt-20 text-center animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl text-foreground/80 italic leading-relaxed">
            "{t('landing.quote_karma')}"
          </blockquote>
          <p className="text-muted-foreground mt-3 text-sm tracking-wide">{t('landing.quote_karma_ref')}</p>
        </div>
      </section>

      {/* Feature Carousel */}
      <FeatureCarousel />

      {/* Features */}
      <section id="features" className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-secondary/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-100/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <div className="mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-primary/70 mb-3 font-medium">{t('landing.how_subtitle')}</p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-foreground">
              {t('landing.features_title')}
            </h2>
            <p className="text-muted-foreground text-lg mt-4 max-w-2xl">
              {t('landing.features_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div
                key={f.titleKey}
                className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-8 group shadow-lg shadow-black/[0.03] hover:shadow-xl hover:shadow-primary/[0.06] transition-all duration-300 hover:-translate-y-1"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                  <f.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-medium text-foreground mb-3">{t(f.titleKey)}</h3>
                <p className="text-muted-foreground leading-relaxed">{t(f.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Dive — What Living Geeta Offers */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-gradient-to-b from-background to-primary/[0.03]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-primary/70 mb-3 font-medium">{t('landing.deep_subtitle')}</p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-4">
              {t('landing.deep_title')}
            </h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto leading-relaxed">
              {t('landing.how_desc')}
            </p>
          </div>

          <div className="space-y-6">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="deep-feature-programs">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-violet-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-medium text-foreground">
                    {dynamicPrograms.length > 0 ? `${dynamicPrograms.length} ${t('landing.deep_programs_title')}` : t('landing.deep_programs_title')}
                  </h3>
                </div>
                <p className="text-muted-foreground leading-relaxed text-sm mb-3">
                  {t('landing.deep_programs_desc')}
                </p>
                {dynamicPrograms.length > 0 ? (
                  <p className="text-xs text-primary/70 font-medium">
                    {dynamicPrograms.slice(0, 4).map(p => p.landing_title || p.title).join(' \u2022 ')}
                    {dynamicPrograms.length > 4 ? ` \u2022 ${t('landing.and_more')}` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-primary/70 font-medium">21-Day Anger Mastery &bull; 14-Day Anxiety Release &bull; 30-Day Discipline &bull; and more</p>
                )}
              </div>

              <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="deep-feature-ai-guide">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-medium text-foreground">{t('landing.deep_guidance_title')}</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed text-sm mb-3">
                  {t('landing.deep_guidance_desc')}
                </p>
                <p className="text-xs text-primary/70 font-medium">{t('landing.deep_guidance_tags')}</p>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow" data-testid="deep-feature-journey">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                  <Map className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-medium text-foreground mb-2">{t('landing.deep_journey_title')}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t('landing.deep_journey_desc')}
                </p>
              </div>

              <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow" data-testid="deep-feature-family">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center mb-4">
                  <Users className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-medium text-foreground mb-2">{t('landing.deep_family_title')}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t('landing.deep_family_desc')}
                </p>
              </div>

              <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow" data-testid="deep-feature-lifestyle">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                  <Apple className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-medium text-foreground mb-2">{t('landing.deep_lifestyle_title')}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t('landing.deep_lifestyle_desc')}
                </p>
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="deep-feature-reflections">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-medium text-foreground">{t('landing.deep_reflections_title')}</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t('landing.deep_reflections_desc')}
                </p>
              </div>

              <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="deep-feature-triggers">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-red-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-medium text-foreground">{t('landing.deep_trigger_full_title')}</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t('landing.deep_trigger_full_desc')}
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Explore Gita Shlokas Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-12 bg-primary/[0.02]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-primary/60 mb-3 font-medium">{t('landing.explore_subtitle')}</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium tracking-tight text-foreground mb-3">
              {t('landing.explore_title')}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t('landing.explore_desc')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { ch: 2, v: 47, line: "कर्मण्येवाधिकारस्ते मा फलेषु कदाचन", en: "You have a right to perform your duty, but never to the fruits of action." },
              { ch: 4, v: 7, line: "यदा यदा हि धर्मस्य ग्लानिर्भवति भारत", en: "Whenever there is a decline in righteousness, I manifest myself." },
              { ch: 2, v: 14, line: "मात्रास्पर्शास्तु कौन्तेय शीतोष्णसुखदुःखदाः", en: "Sensory experiences are fleeting — they come and go. Bear them patiently." },
              { ch: 9, v: 22, line: "अनन्याश्चिन्तयन्तो मां ये जनाः पर्युपासते", en: "Those who worship Me with undivided devotion, I secure their welfare." },
              { ch: 6, v: 5, line: "उद्धरेदात्मनात्मानं नात्मानमवसादयेत्", en: "Elevate yourself through the power of your mind; do not degrade yourself." },
              { ch: 18, v: 66, line: "सर्वधर्मान्परित्यज्य मामेकं शरणं व्रज", en: "Abandon all varieties of dharma and surrender unto Me alone." },
            ].map(v => (
              <Link
                key={`${v.ch}_${v.v}`}
                to={`/verse/${v.ch}/${v.v}`}
                className="group bg-white/80 backdrop-blur-sm border border-border/20 rounded-xl p-5 hover:shadow-md hover:shadow-primary/[0.05] transition-all duration-300 hover:-translate-y-0.5"
                data-testid={`explore-verse-${v.ch}-${v.v}`}
              >
                <span className="text-[10px] uppercase tracking-wider text-primary/50 font-medium">BG {v.ch}.{v.v}</span>
                <p className="font-['Noto_Sans_Devanagari'] text-sm text-foreground/80 mt-2 mb-2 leading-relaxed line-clamp-1">{v.line}</p>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{v.en}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/chapters" className="inline-flex items-center gap-2 bg-white border border-border/40 rounded-full px-6 py-2.5 text-sm font-medium text-foreground hover:shadow-md hover:border-primary/30 transition-all" data-testid="browse-all-chapters-btn">
              <BookOpen className="w-4 h-4 text-primary" strokeWidth={1.5} />
              {t('landing.browse_all')}
            </Link>
            <Link to="/quotes" className="inline-flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-full px-6 py-2.5 text-sm font-medium text-primary hover:shadow-md hover:bg-primary/10 transition-all" data-testid="browse-quotes-btn">
              Top 100 Gita Quotes
            </Link>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-6">
            {t('landing.journey_title')}
          </h2>
          <p className="text-muted-foreground text-lg mb-12 max-w-2xl mx-auto">
            {t('landing.journey_desc')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", labelKey: "landing.journey_step1_label", descKey: "landing.journey_step1_desc" },
              { step: "2", labelKey: "landing.journey_step2_label", descKey: "landing.journey_step2_desc" },
              { step: "3", labelKey: "landing.journey_step3_label", descKey: "landing.journey_step3_desc" },
              { step: "4", labelKey: "landing.journey_step4_label", descKey: "landing.journey_step4_desc" },
            ].map((s, i) => (
              <div 
                key={s.step}
                className="text-center bg-white/40 backdrop-blur-lg border border-white/30 rounded-2xl p-6 shadow-sm"
                data-testid={`journey-step-${i}`}
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 text-primary font-['Playfair_Display'] text-xl font-semibold">
                  {s.step}
                </div>
                <h4 className="font-['Playfair_Display'] text-lg font-medium text-foreground mb-2">{t(s.labelKey)}</h4>
                <p className="text-sm text-muted-foreground">{t(s.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Us Section */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-gradient-to-b from-amber-50/30 to-orange-50/20" id="support" data-testid="support-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-6">
              Free Forever. Supported by You.
            </h2>
            <div className="text-muted-foreground text-base max-w-2xl mx-auto leading-relaxed space-y-4">
              <p>
                <strong className="text-foreground">Living Geeta is completely free</strong> — and always will be. 
                Our mission is to make the profound wisdom of the Bhagavad Gita accessible to everyone, 
                everywhere, in a way that transforms daily life.
              </p>
              <p>
                We believe spiritual guidance should never be behind a paywall. Whether you're seeking peace during 
                difficult times, looking for life's purpose, or wanting to deepen your spiritual practice — 
                Living Geeta is here for you, always.
              </p>
              <p>
                <strong className="text-foreground">If this app has touched your life</strong>, consider supporting us. 
                Your contribution helps us cover the costs of development, AI services, and infrastructure.
              </p>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="features-grid">
            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                <Target className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Guna Profile</h3>
              <p className="text-xs text-muted-foreground">Discover your nature</p>
            </div>

            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Kosha Enhancement</h3>
              <p className="text-xs text-muted-foreground">Balance your five sheaths</p>
            </div>

            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Guidance by Krishna</h3>
              <p className="text-xs text-muted-foreground">AI spiritual guidance</p>
            </div>

            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-5 h-5 text-green-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Transformation Programs</h3>
              <p className="text-xs text-muted-foreground">Structured growth paths</p>
            </div>

            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-3">
                <Globe className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Multilingual</h3>
              <p className="text-xs text-muted-foreground">Available in 8+ languages</p>
            </div>

            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center mx-auto mb-3">
                <Users className="w-5 h-5 text-pink-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Family & Friends</h3>
              <p className="text-xs text-muted-foreground">Share wisdom together</p>
            </div>

            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-3">
                <Flame className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Wellness Journey</h3>
              <p className="text-xs text-muted-foreground">Track spiritual progress</p>
            </div>

            <div className="bg-white/70 backdrop-blur-lg border border-white/40 rounded-xl p-5 text-center hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center mx-auto mb-3">
                <BookOpen className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-foreground text-sm mb-1">Daily Journal</h3>
              <p className="text-xs text-muted-foreground">Reflect and grow daily</p>
            </div>
          </div>

          {/* Support Button */}
          <div className="text-center">
            <Link 
              to="/signup" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full px-8 py-4 text-lg font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all hover:-translate-y-0.5"
              data-testid="support-signup-btn"
            >
              <Heart className="w-5 h-5" />
              Support Us
            </Link>
            <p className="text-muted-foreground text-sm mt-4">
              Non-profit initiative for spiritual growth
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/50 backdrop-blur-2xl border border-white/40 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden shadow-xl shadow-primary/[0.04]">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5" />
            <div className="relative z-10">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium text-foreground mb-4">
                {t('landing.final_cta_title')}
              </h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                {t('landing.final_cta_desc')}
              </p>
              <Link to="/signup" className="bg-primary text-primary-foreground rounded-full px-10 py-4 font-medium hover:shadow-lg hover:shadow-primary/20 transition-all hover:-translate-y-0.5 inline-flex items-center gap-2" data-testid="final-cta-btn">
                {t('landing.final_cta_btn')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
